/**
 * Ferrous bundle widget.
 *
 * Port of the Devjour `mrk.js` quantity-break bundle to the Pitch theme,
 * rewritten without jQuery (Pitch doesn't load it, and 90KB for this would
 * cost more than the widget is worth on an ad landing page).
 *
 * How it works: each tier owns N shade pickers. Whichever tier is selected
 * resolves its pickers to variant ids and writes `items[n][id]` /
 * `items[n][quantity]` hidden inputs into the product form. Shopify's
 * /cart/add accepts that shape for multi-line adds, and Pitch submits the
 * form with `new FormData(form)`, so the inputs go through untouched.
 *
 * The theme's own single-variant `<input name="id">` is disabled while a
 * bundle is active — a disabled input is left out of FormData, so the two
 * never fight over the same cart request.
 *
 * When a set product is configured, tiers with 2+ shades instead add one line
 * of that product's "Set of N" variant, with the shades as a line property, so
 * the cart and checkout show one set at the set's own price. Prices on the
 * bars are labels only and must match that product's variant prices.
 */
(function () {
  'use strict';

  var FORM_SELECTOR = 'form[data-type="add-to-cart-form"]';
  var HIDDEN_CLASS = 'mrk-hidden-inputs';

  function parseVariants(root) {
    var tag = root.querySelector('script[data-ferrous-bundle-variants]');
    if (!tag) return [];
    try {
      return JSON.parse(tag.textContent) || [];
    } catch (e) {
      return [];
    }
  }

  /** Variants of the set product, or [] when none is configured/sellable. */
  function parseSetVariants(root) {
    var tag = root.querySelector('script[data-ferrous-bundle-set]');
    if (!tag) return [];
    try {
      return JSON.parse(tag.textContent) || [];
    } catch (e) {
      return [];
    }
  }

  /** The "Set of N" variant for a tier size, matched on the number in its title. */
  function findSetVariant(setVariants, size) {
    for (var i = 0; i < setVariants.length; i++) {
      var match = String(setVariants[i].title || '').match(/\d+/);
      if (match && Number(match[0]) === size && setVariants[i].available !== false) return setVariants[i];
    }
    return null;
  }

  /** "Electro Likes, Heavy Hitter x2" */
  function describeShades(counts) {
    return counts
      .map(function (row) {
        return row.title + (row.quantity > 1 ? ' x' + row.quantity : '');
      })
      .join(', ');
  }

  /** The product form this widget feeds. Prefers one inside the same section. */
  function findForm(root) {
    var section = root.closest('.shopify-section, [data-section-id]') || document;
    return section.querySelector(FORM_SELECTOR) || document.querySelector(FORM_SELECTOR);
  }

  function hiddenContainer(form) {
    var box = form.querySelector('.' + HIDDEN_CLASS);
    if (!box) {
      box = document.createElement('div');
      box.className = HIDDEN_CLASS;
      box.hidden = true;
      form.appendChild(box);
    }
    return box;
  }

  /** Match a variant by its option values, in option order. */
  function findVariant(variants, selected) {
    for (var i = 0; i < variants.length; i++) {
      var opts = variants[i].options || [];
      if (opts.length !== selected.length) continue;
      var same = true;
      for (var j = 0; j < opts.length; j++) {
        if (String(opts[j]) !== String(selected[j])) {
          same = false;
          break;
        }
      }
      if (same) return variants[i];
    }
    return null;
  }

  function selectedTier(root) {
    var checked = root.querySelector('input[name="mrk-quantity"]:checked');
    if (checked) return checked.closest('.mrk-bundles__bar');
    var first = root.querySelector('input[name="mrk-quantity"]');
    if (first) {
      first.checked = true;
      return first.closest('.mrk-bundles__bar');
    }
    return null;
  }

  /** Resolve one slot's radios to a variant, writing any error into the slot. */
  function resolveSlot(slot, variants) {
    var selected = [];
    slot.querySelectorAll('.frs-shade-picker').forEach(function (picker) {
      var hit = picker.querySelector('input[type="radio"]:checked');
      if (hit) selected.push(hit.value);
    });

    var error = slot.querySelector('.mrk-error');
    if (!selected.length) {
      if (error) error.textContent = '';
      return null;
    }

    var variant = findVariant(variants, selected);
    if (!variant) {
      if (error) error.textContent = selected.join(', ') + ' is unavailable.';
      return null;
    }
    if (variant.available === false) {
      if (error) error.textContent = (variant.title || selected.join(', ')) + ' is sold out.';
      return null;
    }
    if (error) error.textContent = '';
    return variant;
  }

  function hiddenInput(name, value) {
    var el = document.createElement('input');
    el.type = 'hidden';
    el.name = name;
    el.value = value;
    return el;
  }

  /**
   * Mirror the first slot's shade onto the theme's own variant picker.
   *
   * The gallery's colour filter keys off the selected variant, not off this
   * widget, so without this the photos never change when someone picks a
   * shade in the bundle. Devjour did the same thing.
   */
  /**
   * Jump the product gallery to the image for a shade.
   *
   * The bundle page has no variant picker to piggyback on — its gallery is the
   * set's own media — so slides are matched by alt text (which is already the
   * shade name) and driven through the theme slideshow's select(index).
   */
  function showShadeInGallery(shade) {
    var want = String(shade || '').trim().toLowerCase();
    if (!want) return;

    document.querySelectorAll('slideshow-component').forEach(function (slideshow) {
      var slides = slideshow.querySelectorAll('slideshow-slide');
      if (!slides.length || typeof slideshow.select !== 'function') return;

      var index = -1;
      for (var i = 0; i < slides.length; i++) {
        var img = slides[i].querySelector('img[alt]');
        if (img && img.getAttribute('alt').trim().toLowerCase() === want) {
          index = i;
          break;
        }
      }
      if (index === -1) return;

      try {
        slideshow.select(index);
      } catch (e) {
        /* A theme update could change the signature; the pickers still work. */
      }
    });
  }

  var syncing = false;
  function syncThemePicker(slot) {
    if (syncing || !slot) return;
    slot.querySelectorAll('.frs-shade-picker').forEach(function (picker) {
      var chosen = picker.querySelector('input[type="radio"]:checked');
      if (!chosen) return;

      var optionName = picker.dataset.optionName || '';
      // Matched in JS rather than via an attribute selector so shade names
      // with quotes or brackets can't break the query.
      var target = null;
      document.querySelectorAll('input[type="radio"]').forEach(function (input) {
        if (target) return;
        var name = input.getAttribute('name') || '';
        if (name.indexOf(optionName + '-') !== 0) return;
        if (name.slice(-7) !== '-swatch') return;
        if (input.value !== chosen.value) return;
        target = input;
      });

      if (!target || target.checked) return;
      syncing = true;
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
      syncing = false;
    });
  }

  function sync(root, variants, setVariants, form) {
    var tier = selectedTier(root);
    var box = hiddenContainer(form);
    var themeVariantInput = form.querySelector('input[name="id"]');
    box.textContent = '';

    if (!tier) {
      if (themeVariantInput) themeVariantInput.disabled = false;
      return;
    }

    // Collapse duplicate shades into one line with a higher quantity, so
    // picking the same colour twice doesn't create two identical cart lines.
    var counts = [];
    var slots = tier.querySelectorAll('.quantity-break__selector-item');
    var resolved = 0;
    slots.forEach(function (slot, index) {
      if (index === 0) syncThemePicker(slot);
      var variant = resolveSlot(slot, variants);
      if (!variant) return;
      resolved += 1;
      var existing = counts.find(function (row) {
        return row.id === variant.id;
      });
      if (existing) existing.quantity += 1;
      else counts.push({ id: variant.id, quantity: 1, title: variant.title });
    });

    // Every shade picked and a matching set variant exists: one set line.
    var setVariant = slots.length > 1 && resolved === slots.length ? findSetVariant(setVariants, slots.length) : null;
    if (setVariant) {
      box.appendChild(hiddenInput('items[0][id]', setVariant.id));
      box.appendChild(hiddenInput('items[0][quantity]', 1));
      box.appendChild(hiddenInput('items[0][properties][Shades]', describeShades(counts)));
      if (themeVariantInput) themeVariantInput.disabled = true;
      return;
    }

    if (!counts.length) {
      // Nothing resolved — hand control back to the theme's own picker.
      if (themeVariantInput) themeVariantInput.disabled = false;
      return;
    }

    counts.forEach(function (row, i) {
      box.appendChild(hiddenInput('items[' + i + '][id]', row.id));
      box.appendChild(hiddenInput('items[' + i + '][quantity]', row.quantity));
    });

    // `id` and `items[]` in one request is ambiguous to /cart/add, so the
    // theme's input steps aside while the bundle is driving.
    if (themeVariantInput) themeVariantInput.disabled = true;
  }

  function init(root) {
    if (root.dataset.ferrousBundleReady === 'true') return;
    var form = findForm(root);
    if (!form) return;

    var variants = parseVariants(root);
    if (!variants.length) return;
    var setVariants = parseSetVariants(root);

    root.dataset.ferrousBundleReady = 'true';

    var run = function () {
      sync(root, variants, setVariants, form);
    };

    root.addEventListener('change', function (event) {
      if (!event.target.matches('input[name="mrk-quantity"], .frs-shade input[type="radio"]')) return;

      // Show the shade the customer just picked, whichever slot it was in.
      if (event.target.matches('.frs-shade input[type="radio"]')) {
        showShadeInGallery(event.target.value);
      }
      run();
    });

    // Pitch re-renders parts of the product form on variant change; re-assert
    // our inputs afterwards so they aren't dropped.
    document.addEventListener('variant:update', run);

    run();
  }

  function boot() {
    document.querySelectorAll('ferrous-bundle').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: blocks are injected after load.
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:block:select', boot);
})();
